import * as React from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

export type ConfirmDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Styles the confirm action as destructive. */
  destructive?: boolean
  /** Disables the action and shows a pending label while the work runs. */
  isPending?: boolean
  /** Overrides the pending label — not every confirmed action is a delete. */
  pendingLabel?: string
  onConfirm: () => void
}

/**
 * THE confirmation step, for any action worth stopping to think about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE DIALOG, NOT ONE PER CALLER.
 *
 * Every caller supplies its own title, description and button label; nothing
 * else changes. That is the point — a product where a removal asks one way and
 * a sign-out asks another teaches people to click through both without
 * reading, which is exactly the habit a confirmation exists to prevent.
 *
 * NEVER `window.confirm`. The browser's dialog cannot be themed, blocks the
 * event loop, is suppressible by the user, and says "localhost:5173 says" above
 * the question — on a screen that places real orders, a prompt that looks like
 * a browser warning rather than part of the product is one people dismiss.
 *
 * IT DOES NOT CLOSE ITSELF on confirm. The action is usually async, so the
 * caller owns the dialog's lifetime: it stays open with `isPending` while the
 * work runs, and a failure leaves the user looking at the question they just
 * answered rather than at a screen that silently did nothing.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  /*
   * Neutral defaults. They used to read "Delete" / "Deleting…" from this
   * dialog's first caller, which is a trap in a shared component: a caller
   * that forgets the label gets a button offering to delete something.
   */
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  isPending = false,
  pendingLabel = 'Working…',
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(event) => {
              // The dialog would otherwise close itself before the caller has
              // run; keeping it open lets the pending state be visible and
              // lets a failure leave the user where they were.
              event.preventDefault()
              onConfirm()
            }}
            className={cn(
              destructive &&
                'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
            )}
          >
            {isPending ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
